import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type { DepartmentDto, Institution } from '../types/models';

const emptyForm = { institutionId: '', code: '', name: '' };

export function DepartmentsPage() {
  const { orgId = '' } = useParams();
  const toast = useToast();

  const [insts, setInsts] = useState<Institution[] | null>(null);
  const [depts, setDepts] = useState<DepartmentDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Filter bar (Institution).
  const [filterInst, setFilterInst] = useState('');

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function refresh() {
    try {
      const [i, d] = await Promise.all([api.listInstitutions(orgId), api.listDepartments(orgId)]);
      setInsts(i);
      setDepts(d);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load departments.');
    }
  }

  const hasInstitutions = !!insts?.length;

  function openCreate() {
    setFormError(null);
    setEditId(null);
    setForm({ institutionId: insts && insts.length ? insts[0].id : '', code: '', name: '' });
    setShowModal(true);
  }

  function openEdit(d: DepartmentDto) {
    setFormError(null);
    setEditId(d.id);
    setForm({ institutionId: d.institutionId, code: d.code, name: d.name });
    setShowModal(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    try {
      if (editId) await api.updateDepartment(editId, form.code.trim(), form.name.trim());
      else await api.createDepartment(form.institutionId, form.code.trim(), form.name.trim());
      setShowModal(false);
      await refresh();
      toast.success(editId ? 'Department updated.' : 'Department created.');
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save department.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(d: DepartmentDto) {
    if (!confirm(`Delete department “${d.name}”? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteDepartment(d.id);
      await refresh();
      toast.success('Department deleted.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to delete department.';
      setError(msg);
      toast.error(msg);
    }
  }

  const instCode = (id: string) => insts?.find(i => i.id === id)?.code ?? '—';

  const filteredDepts = useMemo(
    () => !depts ? null : depts.filter(d => !filterInst || d.institutionId === filterInst),
    [depts, filterInst],
  );

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Departments</h1>
        <button className="primary" onClick={openCreate} disabled={!hasInstitutions}>+ Add department</button>
      </div>

      {!hasInstitutions && insts && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="muted">
            Create an <Link to={`/org/${orgId}/institutions`}>institution</Link> first — departments live under one.
          </div>
        </div>
      )}

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 240px' }}>
            <div className="muted">Institution</div>
            <select value={filterInst} onChange={e => setFilterInst(e.target.value)}>
              <option value="">All</option>
              {insts?.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
            </select>
          </label>
          {filterInst && <button onClick={() => setFilterInst('')}>Clear</button>}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Institution</th>
              <th>Name</th>
              <th>Code</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!depts && <tr><td colSpan={5} className="muted">Loading…</td></tr>}
            {depts && filteredDepts && filteredDepts.length === 0 && (
              <tr><td colSpan={5} className="muted">
                {depts.length === 0 ? 'No departments yet.' : 'No departments match the selected filter.'}
              </td></tr>
            )}
            {filteredDepts?.map((d, idx) => (
              <tr key={d.id}>
                <td>{idx + 1}</td>
                <td>{instCode(d.institutionId)}</td>
                <td style={{ fontWeight: 600 }}>{d.name}</td>
                <td className="slug">{d.code}</td>
                <td>
                  <button className="link-btn" onClick={() => openEdit(d)}>Edit</button>
                  <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(d)}>Delete</button>
                  <RecordInfoButton entity="departments" id={d.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editId ? 'Edit department' : 'Create department'} onClose={() => setShowModal(false)}>
          <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
            <label>
              <div className="muted">Institution *</div>
              <select value={form.institutionId} required disabled={!!editId}
                      onChange={e => setForm({ ...form, institutionId: e.target.value })}>
                <option value="" disabled>Select…</option>
                {insts?.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
              </select>
            </label>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: '0 0 140px' }}>
                <div className="muted">Code *</div>
                <input value={form.code} required maxLength={32}
                       onChange={e => setForm({ ...form, code: e.target.value })} placeholder="CSE" />
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Name *</div>
                <input value={form.name} required maxLength={200}
                       onChange={e => setForm({ ...form, name: e.target.value })} />
              </label>
            </div>
            {formError && <div className="error">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? 'Saving…' : editId ? 'Save changes' : 'Create department'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </OrgLayout>
  );
}
